import React, { useState, useEffect, useRef } from 'react';
import { MonthField } from '../components/MonthField';
import { RutField, fillEmpty } from '../components/RutField';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import FinancialDashboard from '../components/FinancialDashboard';
import {
  Calculator,
  FileText,
  TrendingUp,
  DollarSign,
  PieChart,
  Send,
  ArrowLeft,
  Building2,
  Calendar,
  Loader2,
  Save,
  FolderOpen,
  Trash2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle,
  Info,
  Zap,
  Receipt,
  Scale,
  Shield,
  Download,
  BarChart3,
  FileDown,
  FileSpreadsheet
} from 'lucide-react';

// Tax regimes options
const TAX_REGIMES = [
  { id: 'pro_pyme_general', name: 'Pro-Pyme General (14D N°3)', rate: '25%' },
  { id: 'pro_pyme_transparente', name: 'Pro-Pyme Transparente (14D N°8)', rate: 'Transparente' },
  { id: 'regimen_general', name: 'Régimen General (14A)', rate: '27%' },
  { id: 'renta_presunta', name: 'Renta Presunta', rate: 'Variable' },
  { id: 'sociedad_profesionales', name: 'Sociedad de Profesionales', rate: 'Progresivo' },
  { id: 'persona_natural', name: 'Persona Natural', rate: 'Progresivo' }
];

// Collapsible section component
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-700/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-blue-400" />
          <span className="font-medium text-white">{title}</span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 pt-0 border-t border-slate-700/50">
          {children}
        </div>
      )}
    </div>
  );
};

// Number input component
const NumberInput = ({ label, value, onChange, placeholder = "0" }) => (
  <div>
    <label className="block text-xs text-slate-400 mb-1">{label}</label>
    <input
      type="number"
      value={value || ''}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      placeholder={placeholder}
      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none"
    />
  </div>
);

// Quick action button
const QuickAction = ({ icon: Icon, label, onClick, color = 'blue' }) => (
  <button
    onClick={onClick}
    className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all hover:scale-105 ${
      color === 'blue' ? 'bg-blue-500/10 border-blue-500/30 text-blue-400 hover:bg-blue-500/20' :
      color === 'green' ? 'bg-green-500/10 border-green-500/30 text-green-400 hover:bg-green-500/20' :
      color === 'purple' ? 'bg-purple-500/10 border-purple-500/30 text-purple-400 hover:bg-purple-500/20' :
      'bg-amber-500/10 border-amber-500/30 text-amber-400 hover:bg-amber-500/20'
    }`}
  >
    <Icon className="w-6 h-6" />
    <span className="text-xs font-medium">{label}</span>
  </button>
);

const AccountingAIPage = () => {
  const { api } = useAuth();
  const chatEndRef = useRef(null);
  
  // State
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const [activeTab, setActiveTab] = useState('input'); // input, results, chat
  const [analysisResult, setAnalysisResult] = useState(null);
  
  // Financial data state
  const [financialData, setFinancialData] = useState({
    company_name: '',
    rut: '',
    tax_regime: 'pro_pyme_general',
    contributor_type: 'empresa',
    period: new Date().toISOString().slice(0, 7), // YYYY-MM
    currency: 'CLP',
    income: {
      sales_with_iva: 0,
      sales_exempt: 0,
      other_income: 0
    },
    costs: {
      cost_of_sales: 0,
      purchases_with_iva: 0,
      purchases_exempt: 0
    },
    expenses: {
      salaries: 0,
      rent: 0,
      utilities: 0,
      marketing: 0,
      professional_services: 0,
      office_supplies: 0,
      depreciation: 0,
      financial_expenses: 0,
      other_expenses: 0
    },
    assets: {
      cash: 0,
      accounts_receivable: 0,
      inventory: 0,
      fixed_assets: 0,
      other_assets: 0
    },
    liabilities: {
      accounts_payable: 0,
      bank_loans: 0,
      taxes_payable: 0,
      other_liabilities: 0
    },
    equity: {
      capital: 0,
      retained_earnings: 0,
      current_year_result: 0
    },
    taxes: {
      iva_debito: 0,
      iva_credito: 0,
      ppm_paid: 0,
      withholdings: 0,
      tax_credits: 0,
      loss_carryforward: 0
    },
    payroll: {
      salaries: 0,
      employer_contributions: 0,
      honorarios: 0
    }
  });
  
  // Update nested state helper
  const updateNestedField = (category, field, value) => {
    setFinancialData(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };
  
  // Scroll to bottom of chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);
  
  // Fetch sessions on load
  useEffect(() => {
    fetchSessions();
  }, []);
  
  const fetchSessions = async () => {
    try {
      const response = await api.get('/accounting/sessions');
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };
  
  const saveSession = async () => {
    try {
      const name = prompt('Nombre de la sesión:', `Sesión ${financialData.company_name || new Date().toLocaleDateString()}`);
      if (!name) return;
      
      const response = await api.post('/accounting/sessions', {
        name,
        financial_data: financialData
      });
      
      setSessionId(response.data.id);
      toast.success('Sesión guardada');
      fetchSessions();
    } catch (error) {
      toast.error('Error al guardar sesión');
    }
  };
  
  const loadSession = async (session) => {
    setFinancialData(session.financial_data);
    setSessionId(session.id);
    setShowSessions(false);
    toast.success(`Sesión "${session.name}" cargada`);
  };
  
  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    if (!confirm('¿Eliminar esta sesión?')) return;
    
    try {
      await api.delete(`/accounting/sessions/${sessionId}`);
      toast.success('Sesión eliminada');
      fetchSessions();
    } catch (error) {
      toast.error('Error al eliminar sesión');
    }
  };
  
  // Analysis functions
  const runAnalysis = async (type) => {
    setAnalyzing(true);
    setActiveTab('results');
    
    const messages = {
      balance: 'Genera el balance contable completo con estado de resultados, flujo de caja e indicadores.',
      taxes: 'Calcula los impuestos estimados del período: IVA, PPM, Primera Categoría.',
      optimize: 'Identifica oportunidades de optimización tributaria legal para este período.',
      full: 'Realiza un análisis contable y tributario completo con recomendaciones.'
    };
    
    try {
      const response = await api.post('/accounting/analyze', {
        message: messages[type] || messages.full,
        financial_data: financialData
      });
      
      setAnalysisResult(response.data);
      toast.success('Análisis completado');
    } catch (error) {
      toast.error('Error en el análisis');
      console.error(error);
    } finally {
      setAnalyzing(false);
    }
  };
  
  // PDF Download functions
  const downloadPDF = async (reportType) => {
    try {
      toast.info(`Generando PDF de ${reportType}...`);
      
      const endpoints = {
        balance: '/accounting/pdf/balance',
        income_statement: '/accounting/pdf/income-statement',
        tax_summary: '/accounting/pdf/tax-summary',
        complete: '/accounting/pdf/complete'
      };
      
      const response = await api.post(endpoints[reportType], {
        financial_data: financialData,
        report_type: reportType
      }, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const companyName = financialData.company_name?.replace(/\s+/g, '_') || 'empresa';
      const period = financialData.period || 'periodo';
      const filenames = {
        balance: `balance_general_${companyName}_${period}.pdf`,
        income_statement: `estado_resultados_${companyName}_${period}.pdf`,
        tax_summary: `resumen_tributario_${companyName}_${period}.pdf`,
        complete: `informe_completo_${companyName}_${period}.pdf`
      };
      
      link.download = filenames[reportType];
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descargado exitosamente');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Error al generar el PDF');
    }
  };
  
  // Excel Download functions
  const downloadExcel = async (reportType) => {
    try {
      toast.info(`Generando Excel de ${reportType}...`);
      
      const endpoints = {
        balance: '/accounting/excel/balance',
        income_statement: '/accounting/excel/income-statement',
        tax_summary: '/accounting/excel/tax-summary',
        complete: '/accounting/excel/complete'
      };
      
      const response = await api.post(endpoints[reportType], {
        financial_data: financialData,
        report_type: reportType
      }, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const companyName = financialData.company_name?.replace(/\s+/g, '_') || 'empresa';
      const period = financialData.period || 'periodo';
      const filenames = {
        balance: `balance_general_${companyName}_${period}.xlsx`,
        income_statement: `estado_resultados_${companyName}_${period}.xlsx`,
        tax_summary: `resumen_tributario_${companyName}_${period}.xlsx`,
        complete: `informe_completo_${companyName}_${period}.xlsx`
      };
      
      link.download = filenames[reportType];
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel descargado exitosamente');
    } catch (error) {
      console.error('Error downloading Excel:', error);
      toast.error('Error al generar el Excel');
    }
  };
  
  // Chat function
  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = chatInput;
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);
    
    try {
      const response = await api.post('/accounting/chat', {
        message: userMessage,
        financial_data: financialData,
        session_id: sessionId
      });
      
      setChatMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
      setSessionId(response.data.session_id);
    } catch (error) {
      toast.error('Error en el chat');
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Lo siento, ocurrió un error. Por favor intenta de nuevo.' 
      }]);
    } finally {
      setLoading(false);
    }
  };
  
  // Quick calculations
  const quickCalculateIVA = async () => {
    try {
      const response = await api.post('/accounting/quick-calc', {
        calculation_type: 'iva',
        sales_with_iva: financialData.income.sales_with_iva,
        purchases_with_iva: financialData.costs.purchases_with_iva
      });
      
      const result = response.data.result;
      toast.success(`IVA Neto: $${result.iva_neto.toLocaleString()}`);
      
      // Update taxes in state
      setFinancialData(prev => ({
        ...prev,
        taxes: {
          ...prev.taxes,
          iva_debito: result.iva_debito,
          iva_credito: result.iva_credito
        }
      }));
    } catch (error) {
      toast.error('Error calculando IVA');
    }
  };
  
  // Calculate totals
  const totalIncome = (financialData.income.sales_with_iva || 0) + 
                      (financialData.income.sales_exempt || 0) + 
                      (financialData.income.other_income || 0);
  
  const totalExpenses = Object.values(financialData.expenses).reduce((sum, val) => sum + (val || 0), 0);
  
  const totalAssets = Object.values(financialData.assets).reduce((sum, val) => sum + (val || 0), 0);
  
  const totalLiabilities = Object.values(financialData.liabilities).reduce((sum, val) => sum + (val || 0), 0);
  
  return (
    <div className="min-h-screen bg-background" data-testid="accounting-ai-page">
      {/* Header */}
      <header className="bg-card border-b border-border px-4 py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                <Calculator className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-lg">Contabilidad IA</h1>
                <p className="text-xs text-slate-400">Asistente contable y tributario chileno</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSessions(!showSessions)}
              className="flex items-center gap-2 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm hover:bg-slate-700 transition-colors"
            >
              <FolderOpen className="w-4 h-4" />
              <span className="hidden sm:inline">Sesiones</span>
            </button>
            <button
              onClick={saveSession}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 rounded-lg text-sm hover:bg-blue-700 transition-colors"
            >
              <Save className="w-4 h-4" />
              <span className="hidden sm:inline">Guardar</span>
            </button>
          </div>
        </div>
      </header>
      
      {/* Sessions panel */}
      {showSessions && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="font-semibold">Sesiones Guardadas</h3>
              <button onClick={() => setShowSessions(false)} className="text-slate-400 hover:text-white">
                ✕
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {sessions.length === 0 ? (
                <p className="text-center text-slate-400 py-8">No hay sesiones guardadas</p>
              ) : (
                <div className="space-y-2">
                  {sessions.map((session) => (
                    <div
                      key={session.id}
                      onClick={() => loadSession(session)}
                      className="flex items-center justify-between p-3 bg-slate-800 rounded-lg cursor-pointer hover:bg-slate-700 transition-colors"
                    >
                      <div>
                        <p className="font-medium text-sm">{session.name}</p>
                        <p className="text-xs text-slate-400">
                          {new Date(session.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={(e) => deleteSession(session.id, e)}
                        className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <QuickAction
            icon={FileText}
            label="Balance Completo"
            onClick={() => runAnalysis('balance')}
            color="blue"
          />
          <QuickAction
            icon={Receipt}
            label="Calcular Impuestos"
            onClick={() => runAnalysis('taxes')}
            color="green"
          />
          <QuickAction
            icon={TrendingUp}
            label="Optimizar Tributación"
            onClick={() => runAnalysis('optimize')}
            color="purple"
          />
          <QuickAction
            icon={Zap}
            label="Análisis Completo"
            onClick={() => runAnalysis('full')}
            color="amber"
          />
        </div>
        
        {/* Tab navigation */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-2 overflow-x-auto">
          <button
            onClick={() => setActiveTab('input')}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'input' 
                ? 'bg-blue-500/20 text-blue-400 border-b-2 border-blue-500' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Datos Financieros
          </button>
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'dashboard' 
                ? 'bg-emerald-500/20 text-emerald-400 border-b-2 border-emerald-500' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            Dashboard Visual
          </button>
          <button
            onClick={() => setActiveTab('results')}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'results' 
                ? 'bg-blue-500/20 text-blue-400 border-b-2 border-blue-500' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Resultados IA
          </button>
          <button
            onClick={() => setActiveTab('chat')}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'chat' 
                ? 'bg-blue-500/20 text-blue-400 border-b-2 border-blue-500' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Chat IA
          </button>
        </div>
        
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <FinancialDashboard financialData={financialData} />
          </div>
        )}
        
        {/* Input Tab */}
        {activeTab === 'input' && (
          <div className="space-y-4">
            {/* Company Info */}
            <CollapsibleSection title="Información de la Empresa" icon={Building2} defaultOpen={true}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Nombre Empresa</label>
                  <input
                    type="text"
                    value={financialData.company_name}
                    onChange={(e) => setFinancialData(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="Mi Empresa SpA"
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">RUT</label>
                  <RutField
                    testid="accounting-rut-input"
                    value={financialData.rut}
                    onChange={(v) => setFinancialData(prev => ({ ...prev, rut: v }))}
                    onLookup={(d) => setFinancialData(prev => fillEmpty(prev, { company_name: d.name }))}
                    placeholder="76.XXX.XXX-X"
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Régimen Tributario</label>
                  <select
                    value={financialData.tax_regime}
                    onChange={(e) => setFinancialData(prev => ({ ...prev, tax_regime: e.target.value }))}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none"
                  >
                    {TAX_REGIMES.map(regime => (
                      <option key={regime.id} value={regime.id}>
                        {regime.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Período</label>
                  <MonthField testid="accounting-period" value={financialData.period} onChange={(v) => setFinancialData(prev => ({ ...prev, period: v }))} />
                </div>
              </div>
            </CollapsibleSection>
            
            {/* Income */}
            <CollapsibleSection title="Ingresos" icon={TrendingUp} defaultOpen={true}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                <NumberInput
                  label="Ventas Afectas a IVA"
                  value={financialData.income.sales_with_iva}
                  onChange={(val) => updateNestedField('income', 'sales_with_iva', val)}
                />
                <NumberInput
                  label="Ventas Exentas"
                  value={financialData.income.sales_exempt}
                  onChange={(val) => updateNestedField('income', 'sales_exempt', val)}
                />
                <NumberInput
                  label="Otros Ingresos"
                  value={financialData.income.other_income}
                  onChange={(val) => updateNestedField('income', 'other_income', val)}
                />
              </div>
              <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <p className="text-sm text-green-400">
                  <strong>Total Ingresos:</strong> ${totalIncome.toLocaleString()}
                </p>
              </div>
            </CollapsibleSection>
            
            {/* Costs */}
            <CollapsibleSection title="Costos" icon={DollarSign}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                <NumberInput
                  label="Costo de Ventas"
                  value={financialData.costs.cost_of_sales}
                  onChange={(val) => updateNestedField('costs', 'cost_of_sales', val)}
                />
                <NumberInput
                  label="Compras Afectas a IVA"
                  value={financialData.costs.purchases_with_iva}
                  onChange={(val) => updateNestedField('costs', 'purchases_with_iva', val)}
                />
                <NumberInput
                  label="Compras Exentas"
                  value={financialData.costs.purchases_exempt}
                  onChange={(val) => updateNestedField('costs', 'purchases_exempt', val)}
                />
              </div>
            </CollapsibleSection>
            
            {/* Expenses */}
            <CollapsibleSection title="Gastos" icon={Receipt}>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
                <NumberInput
                  label="Sueldos"
                  value={financialData.expenses.salaries}
                  onChange={(val) => updateNestedField('expenses', 'salaries', val)}
                />
                <NumberInput
                  label="Arriendo"
                  value={financialData.expenses.rent}
                  onChange={(val) => updateNestedField('expenses', 'rent', val)}
                />
                <NumberInput
                  label="Servicios Básicos"
                  value={financialData.expenses.utilities}
                  onChange={(val) => updateNestedField('expenses', 'utilities', val)}
                />
                <NumberInput
                  label="Marketing"
                  value={financialData.expenses.marketing}
                  onChange={(val) => updateNestedField('expenses', 'marketing', val)}
                />
                <NumberInput
                  label="Servicios Profesionales"
                  value={financialData.expenses.professional_services}
                  onChange={(val) => updateNestedField('expenses', 'professional_services', val)}
                />
                <NumberInput
                  label="Insumos Oficina"
                  value={financialData.expenses.office_supplies}
                  onChange={(val) => updateNestedField('expenses', 'office_supplies', val)}
                />
                <NumberInput
                  label="Depreciación"
                  value={financialData.expenses.depreciation}
                  onChange={(val) => updateNestedField('expenses', 'depreciation', val)}
                />
                <NumberInput
                  label="Gastos Financieros"
                  value={financialData.expenses.financial_expenses}
                  onChange={(val) => updateNestedField('expenses', 'financial_expenses', val)}
                />
              </div>
              <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-400">
                  <strong>Total Gastos:</strong> ${totalExpenses.toLocaleString()}
                </p>
              </div>
            </CollapsibleSection>
            
            {/* Assets */}
            <CollapsibleSection title="Activos" icon={Scale}>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-4">
                <NumberInput
                  label="Caja y Bancos"
                  value={financialData.assets.cash}
                  onChange={(val) => updateNestedField('assets', 'cash', val)}
                />
                <NumberInput
                  label="Cuentas por Cobrar"
                  value={financialData.assets.accounts_receivable}
                  onChange={(val) => updateNestedField('assets', 'accounts_receivable', val)}
                />
                <NumberInput
                  label="Inventario"
                  value={financialData.assets.inventory}
                  onChange={(val) => updateNestedField('assets', 'inventory', val)}
                />
                <NumberInput
                  label="Activo Fijo"
                  value={financialData.assets.fixed_assets}
                  onChange={(val) => updateNestedField('assets', 'fixed_assets', val)}
                />
                <NumberInput
                  label="Otros Activos"
                  value={financialData.assets.other_assets}
                  onChange={(val) => updateNestedField('assets', 'other_assets', val)}
                />
              </div>
              <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-sm text-blue-400">
                  <strong>Total Activos:</strong> ${totalAssets.toLocaleString()}
                </p>
              </div>
            </CollapsibleSection>
            
            {/* Liabilities */}
            <CollapsibleSection title="Pasivos" icon={AlertTriangle}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
                <NumberInput
                  label="Cuentas por Pagar"
                  value={financialData.liabilities.accounts_payable}
                  onChange={(val) => updateNestedField('liabilities', 'accounts_payable', val)}
                />
                <NumberInput
                  label="Préstamos Bancarios"
                  value={financialData.liabilities.bank_loans}
                  onChange={(val) => updateNestedField('liabilities', 'bank_loans', val)}
                />
                <NumberInput
                  label="Impuestos por Pagar"
                  value={financialData.liabilities.taxes_payable}
                  onChange={(val) => updateNestedField('liabilities', 'taxes_payable', val)}
                />
                <NumberInput
                  label="Otros Pasivos"
                  value={financialData.liabilities.other_liabilities}
                  onChange={(val) => updateNestedField('liabilities', 'other_liabilities', val)}
                />
              </div>
              <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-sm text-amber-400">
                  <strong>Total Pasivos:</strong> ${totalLiabilities.toLocaleString()}
                </p>
              </div>
            </CollapsibleSection>
            
            {/* Equity */}
            <CollapsibleSection title="Patrimonio" icon={Shield}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                <NumberInput
                  label="Capital"
                  value={financialData.equity.capital}
                  onChange={(val) => updateNestedField('equity', 'capital', val)}
                />
                <NumberInput
                  label="Utilidades Retenidas"
                  value={financialData.equity.retained_earnings}
                  onChange={(val) => updateNestedField('equity', 'retained_earnings', val)}
                />
                <NumberInput
                  label="Resultado del Ejercicio"
                  value={financialData.equity.current_year_result}
                  onChange={(val) => updateNestedField('equity', 'current_year_result', val)}
                />
              </div>
            </CollapsibleSection>
            
            {/* Taxes */}
            <CollapsibleSection title="Impuestos" icon={Receipt}>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4">
                <NumberInput
                  label="IVA Débito Fiscal"
                  value={financialData.taxes.iva_debito}
                  onChange={(val) => updateNestedField('taxes', 'iva_debito', val)}
                />
                <NumberInput
                  label="IVA Crédito Fiscal"
                  value={financialData.taxes.iva_credito}
                  onChange={(val) => updateNestedField('taxes', 'iva_credito', val)}
                />
                <NumberInput
                  label="PPM Pagados"
                  value={financialData.taxes.ppm_paid}
                  onChange={(val) => updateNestedField('taxes', 'ppm_paid', val)}
                />
              </div>
              <button
                onClick={quickCalculateIVA}
                className="mt-3 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm flex items-center gap-2 transition-colors"
              >
                <Calculator className="w-4 h-4" />
                Calcular IVA Automático
              </button>
            </CollapsibleSection>
          </div>
        )}
        
        {/* Results Tab */}
        {activeTab === 'results' && (
          <div className="space-y-6">
            {analyzing ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="w-12 h-12 animate-spin text-blue-500 mb-4" />
                <p className="text-slate-400">Analizando datos financieros...</p>
              </div>
            ) : analysisResult ? (
              <div className="space-y-6">
                {/* Routing info */}
                {analysisResult.routing && (
                  <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <Info className="w-5 h-5 text-blue-400" />
                        <span className="font-medium">Análisis Ejecutado</span>
                      </div>
                      {/* Download Buttons */}
                      <div className="flex flex-wrap gap-2">
                        {/* PDF Downloads */}
                        <div className="flex gap-1 items-center">
                          <span className="text-xs text-slate-400 mr-1">PDF:</span>
                          <button
                            onClick={() => downloadPDF('balance')}
                            className="flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded text-xs transition-colors"
                            title="Descargar Balance PDF"
                          >
                            <Download className="w-3 h-3" />
                            Balance
                          </button>
                          <button
                            onClick={() => downloadPDF('income_statement')}
                            className="flex items-center gap-1 px-2 py-1 bg-green-600 hover:bg-green-700 rounded text-xs transition-colors"
                            title="Descargar Estado de Resultados PDF"
                          >
                            <Download className="w-3 h-3" />
                            Resultados
                          </button>
                          <button
                            onClick={() => downloadPDF('complete')}
                            className="flex items-center gap-1 px-2 py-1 bg-amber-600 hover:bg-amber-700 rounded text-xs transition-colors"
                            title="Descargar Informe Completo PDF"
                          >
                            <FileDown className="w-3 h-3" />
                            Completo
                          </button>
                        </div>
                        {/* Excel Downloads */}
                        <div className="flex gap-1 items-center border-l border-slate-600 pl-2">
                          <span className="text-xs text-slate-400 mr-1">Excel:</span>
                          <button
                            onClick={() => downloadExcel('balance')}
                            className="flex items-center gap-1 px-2 py-1 bg-emerald-600 hover:bg-emerald-700 rounded text-xs transition-colors"
                            title="Descargar Balance Excel"
                          >
                            <FileSpreadsheet className="w-3 h-3" />
                            Balance
                          </button>
                          <button
                            onClick={() => downloadExcel('income_statement')}
                            className="flex items-center gap-1 px-2 py-1 bg-teal-600 hover:bg-teal-700 rounded text-xs transition-colors"
                            title="Descargar Estado de Resultados Excel"
                          >
                            <FileSpreadsheet className="w-3 h-3" />
                            Resultados
                          </button>
                          <button
                            onClick={() => downloadExcel('complete')}
                            className="flex items-center gap-1 px-2 py-1 bg-cyan-600 hover:bg-cyan-700 rounded text-xs transition-colors"
                            title="Descargar Informe Completo Excel"
                          >
                            <FileSpreadsheet className="w-3 h-3" />
                            Completo
                          </button>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-slate-400 mt-2">
                      Función: {analysisResult.routing.funcion} — {analysisResult.routing.motivo}
                    </p>
                  </div>
                )}
                
                {/* Results content */}
                {analysisResult.results?.balance && (
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-400" />
                      Balance y Estado de Resultados
                    </h3>
                    <div className="prose prose-invert max-w-none">
                      <ReactMarkdown>{analysisResult.results.balance}</ReactMarkdown>
                    </div>
                  </div>
                )}
                
                {analysisResult.results?.taxes && (
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Receipt className="w-5 h-5 text-green-400" />
                      Cálculo de Impuestos
                    </h3>
                    <div className="prose prose-invert max-w-none">
                      <ReactMarkdown>{analysisResult.results.taxes}</ReactMarkdown>
                    </div>
                  </div>
                )}
                
                {analysisResult.results?.optimization && (
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-purple-400" />
                      Optimización Tributaria
                    </h3>
                    <div className="prose prose-invert max-w-none">
                      <ReactMarkdown>{analysisResult.results.optimization}</ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-16">
                <PieChart className="w-16 h-16 mx-auto text-slate-600 mb-4" />
                <h3 className="text-lg font-medium mb-2">Sin resultados aún</h3>
                <p className="text-slate-400 mb-4">
                  Ingresa tus datos financieros y ejecuta un análisis
                </p>
                <button
                  onClick={() => setActiveTab('input')}
                  className="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Ir a Datos Financieros
                </button>
              </div>
            )}
          </div>
        )}
        
        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <div className="flex flex-col h-[calc(100vh-300px)]">
            {/* Chat messages */}
            <div className="flex-1 overflow-y-auto space-y-4 mb-4">
              {chatMessages.length === 0 ? (
                <div className="text-center py-12">
                  <Calculator className="w-12 h-12 mx-auto text-slate-600 mb-4" />
                  <h3 className="text-lg font-medium mb-2">Chat de Contabilidad IA</h3>
                  <p className="text-slate-400 text-sm max-w-md mx-auto">
                    Pregúntame sobre tus datos financieros, impuestos, deducciones permitidas, 
                    o cualquier consulta contable relacionada con la normativa chilena.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center mt-4">
                    {[
                      '¿Cómo optimizo mi IVA?',
                      '¿Qué gastos puedo deducir?',
                      '¿Cuánto debo pagar de PPM?',
                      'Explica el régimen Pro-Pyme'
                    ].map((suggestion) => (
                      <button
                        key={suggestion}
                        onClick={() => setChatInput(suggestion)}
                        className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-full text-xs hover:bg-slate-700 transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                chatMessages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        msg.role === 'user'
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-800 border border-slate-700'
                      }`}
                    >
                      {msg.role === 'assistant' ? (
                        <div className="prose prose-invert prose-sm max-w-none">
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </div>
                      ) : (
                        <p className="text-sm">{msg.content}</p>
                      )}
                    </div>
                  </div>
                ))
              )}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-slate-800 border border-slate-700 rounded-2xl px-4 py-3">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            
            {/* Chat input */}
            <div className="flex gap-2">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                placeholder="Escribe tu consulta contable o tributaria..."
                className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                disabled={loading}
              />
              <button
                onClick={sendChatMessage}
                disabled={loading || !chatInput.trim()}
                className="px-4 py-3 bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AccountingAIPage;
